import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomPagination } from '../../../../../../shared-components/pagination/CustomPagination'
import { Title } from '../../Title'
import styles from './Invoices.module.scss'
import instance from '../../../../../../../providers/axios.config'
import Notiflix from 'notiflix'
import Skeleton from '@mui/material/Skeleton'
import { Iinvoice, typeResponse } from '../../../../../../../utils/types'
import { BASE_URL, PAID, PENDING, REFUNDED, STRIPE_PAYMENT_TYPE } from '../../../../../../../utils/constants'
import { formatDate } from '../../../../../../../utils/helpers'

interface IInvoicesResponse extends typeResponse {
  data:{
    invoices: Iinvoice[],
    pagination: {
      currentPage: number,
      perPage: number,
      totalPages: number
    }
  }
}

export const Invoices = () => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [invoices, setInvoices] = useState<Iinvoice[]>([])
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    totalPages: 1
  })
  const handleChange = (event: any, value: any) => {
    setPagination({...pagination, currentPage: value})
  }
  const getMyInvoices = async () => {
    setIsLoading(true)
    try {
      const res = await instance.get(`invoices?limit=${pagination.perPage}&page=${pagination.currentPage}`) as IInvoicesResponse
      setTimeout(() => {
        setInvoices(res?.data?.invoices || [])
        setPagination({ ...res?.data?.pagination })
        setIsLoading(false)
      }, 300)
    } catch (error) {
      // Show error notification
      Notiflix.Report.failure(
        'Error',
        'Failed to fetch data. Please try again later.',
        'OK'
      )
      setIsLoading(false);
    }
  }
  useEffect(() => {
    getMyInvoices()
  }, [pagination.currentPage])

  const handleDownload = async (id: string) => {
    try {
      const response = await instance.get(`invoices/${id}/download`, {responseType: 'arraybuffer'}) as any
      let blob = new Blob([response], {type: 'application/pdf'});
        let url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `INV-${id}.pdf`)
      document.body.appendChild(link)
      link.click()
    } catch (e) {
      Notiflix.Report.failure(
        'Error',
        `${t('error_api')}`,
        'OK'
      )
    }
  }

  const handlePay = async (planUuid: string, userSubscriptionUuid: string) => {
    try {
      const requestData: {
        plan: string;
        payment_method: string;
        url: string;
        user_subscription_uuid: string;
      } = {
        plan: planUuid,
        payment_method: STRIPE_PAYMENT_TYPE,
        url: `${BASE_URL}plans-prices?plan`,
        user_subscription_uuid: userSubscriptionUuid
      };
      const response = await instance.post(`subscriptions`, {...requestData}) as any
      if(response && typeof response === 'string') {
        window.open(response,"_self")
      }
      // if(payment === 'paypal' && response?.links?.length) {
      //   window.open(response?.links[1].href, '_self')
      // }
    } catch (error: any) {
      Notiflix.Report.failure(
        'Error',
        `${error?.response?.data?.message}`,
        'OK'
      )
    }
  }
  
  const handlDeleteInvoice = async (invoice: Iinvoice) => {
    try {
      const response = await instance.delete(`invoices/${invoice.id}/delete`) as any
      if (response.status === 'success') {
        setInvoices((prevState) => {
          return prevState.filter((inv) => inv.id !== invoice.id);
        })
      }
      if (response.status === 'error') {
          Notiflix.Report.failure(
          'Error',
          `${response.message}`,
          'OK'
        )
      }
    } catch (error: any) {
      Notiflix.Report.failure(
        'Error',
        `${error?.response?.data?.message}`,
        'OK'
      )
    }
  }

  const renderInvoicePaymentStatus = (paymentStatus: string, paymentStatusLabel: string) => {
    switch (paymentStatus) {
      case PENDING:
        return <Chip label={paymentStatusLabel} sx={{ color: 'orange' }} size='small' />
      case PAID:
        return <Chip label={paymentStatusLabel} sx={{ color: 'green' }} size='small' />
      case REFUNDED:
        return <Chip label={paymentStatusLabel} color='primary' size='small' />
    }
  }

  const renderTableInvoice = () => {
    return (
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow key={invoice.id} className={styles.tableRow}>
            <TableCell>
              <Tooltip title={invoice?.name} placement="top-start">
                <Typography variant='body2' noWrap className={styles.ellipsis}>{invoice?.name}</Typography>
              </Tooltip>
            </TableCell>
            <TableCell>{formatDate(invoice?.updatedAt || '', true)}</TableCell>
            <TableCell>{renderInvoicePaymentStatus(invoice.payment_status || PENDING, t(invoice.payment_status || PENDING))}</TableCell>
            <TableCell>{invoice?.total}</TableCell>
            <TableCell className={styles.downloadInvoiceCell}>
              {invoice.payment_status && [PAID, REFUNDED].indexOf(invoice.payment_status) !== -1 && (
                <Button variant='contained' onClick={() => handleDownload(invoice?.id)} className={styles.textButton}>
                  <Typography color='white' variant='body2'>INV-S-{invoice?.id}</Typography>
                </Button>
              )}
            </TableCell>
            <TableCell>
              {invoice.payment_status === PENDING && (
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'start'} gap={1}>
                  <Button
                    variant='contained'
                    color='success'
                    className={styles.textfieldButton}
                    size='small'
                    onClick={() => invoice?.subscription && invoice?.plan ? handlePay(invoice.plan.uuid, invoice.subscription.uuid) : {}}
                  >
                    {`${t('pay')} (${invoice?.total} EUR)`}
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    className={styles.textfieldButton}
                    size='small'
                    onClick={() => handlDeleteInvoice(invoice)}
                  >
                    {t('Delete')}
                  </Button>
                </Box>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    )
  }
  const renderTableSkeleton = () => {
    return (
      <TableBody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((number) => (
          <TableRow key={number} className={styles.tableRow}>
            <TableCell><Skeleton /></TableCell>
            <TableCell><Skeleton /></TableCell>
            <TableCell><Skeleton /></TableCell>
            <TableCell><Skeleton /></TableCell>
            <TableCell className={styles.downloadInvoiceCell}>
              <Button variant='contained' className={styles.textButton}>
                <Typography color='white' variant='body2'><Skeleton /></Typography>
              </Button>
            </TableCell>
            <TableCell><Skeleton /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    )
  }
  if(!invoices.length) {
    return (
      <div className={styles.container}>
        <div className={styles.topContainer}>
          <Title title={t('My Invoices')} />
        </div>

        <div className={styles.content}>
          <div className={styles.title}>{t('Purchase History')}</div>

          <div className={styles.table}>
            {t('no_invoices')}
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <Title title={t('My Invoices')} />
      </div>

      <div className={styles.content}>
        <div className={styles.title}>{t('Purchase History')}</div>

        <div className={styles.table}>
          <TableContainer component={Paper} className={styles.table}>
            <Table>
              <TableHead>
                <TableRow className={styles.tableRow}>
                  <TableCell className={styles.titleCell}>
                    {t('Purchase Description')}
                  </TableCell>
                  <TableCell className={styles.titleCell}>
                    {t('Date')}
                  </TableCell>
                  <TableCell className={styles.titleCell}>
                    {t('status')}
                  </TableCell>
                  <TableCell className={styles.titleCell}>
                    {t('Total')}
                  </TableCell>
                  <TableCell className={styles.titleCell}>
                    {t('Download Invoice')}
                  </TableCell>
                  <TableCell className={styles.titleCell}></TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? renderTableSkeleton() : renderTableInvoice()}
            </Table>
          </TableContainer>

          {pagination?.totalPages > 1 && <CustomPagination
            shape='rounded'
            count={pagination?.totalPages}
            page={pagination?.currentPage}
            onChange={handleChange}
            size='large'
            className={styles.pagination}
          />}
        </div>
      </div>
    </div>
  )
}
